import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import Map, { Source, Layer } from 'react-map-gl/maplibre';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Search, X, Loader2, Globe2 } from 'lucide-react';
import { BASEMAPS } from '../../../data/mockData';
import {
  computeDiscreteColorBands,
  getLayerColorProperty,
  getLayerNumClasses,
  propertyGetExpression,
  MAP_MAX_FEATURES_PER_LAYER_DEFAULT,
  prepareGeoJSONForMap,
  sliceGeoJsonForDisplay
} from '../../../utils/mapUtils';
import { mapApi } from '../../../api/mapApi';
import { MAP_WEBGL_UNAVAILABLE_MESSAGE } from '../../../constants/mapMessages';
import { getPaletteColors, getDefaultPalette, mapIntervalToColorIndex } from '../../../utils/colorPalettes';
import { cn } from '../../../lib/utils';
import MapFeaturePopup from './MapFeaturePopup';

// Helper to clamp numeric values to safe range for protobuf varint encoding
const clampToSafeRange = (value) => {
  if (typeof value !== 'number' || !isFinite(value)) {
    return value;
  }
  const MAX_SAFE = Number.MAX_SAFE_INTEGER;
  const MIN_SAFE = -Number.MAX_SAFE_INTEGER;

  if (value > MAX_SAFE) return MAX_SAFE;
  if (value < MIN_SAFE) return MIN_SAFE;
  return value;
};

// Helper to build filter condition expression for MapLibre
const buildFilterCondition = (valueFilter, layer) => {
  if (!valueFilter) return null;

  const colorProp = getLayerColorProperty(layer);
  if (!colorProp) return null;

  const propExpr = propertyGetExpression(colorProp);

  // Clamp filter values to safe range to prevent protobuf varint encoding errors
  const safeMin = typeof valueFilter.min === 'number' ? clampToSafeRange(valueFilter.min) : valueFilter.min;
  const safeMax = typeof valueFilter.max === 'number' ? clampToSafeRange(valueFilter.max) : valueFilter.max;

  const isExactMatch = safeMin === safeMax;
  const upperBound = (isExactMatch || valueFilter.isLast)
    ? ['<=', propExpr, safeMax]
    : ['<', propExpr, safeMax];

  return isExactMatch
    ? ['==', propExpr, safeMin]
    : ['all', ['>=', propExpr, safeMin], upperBound];
};

// MapLibre expression for discrete color bands (no interpolation)
// Uses the exact same color bands as the legend
const getLayerColor = (layer) => {
  const min = layer.stats?.min ?? 0;
  const max = layer.stats?.max ?? 100;

  // Compute discrete color bands using shared function (same as Colorbar)
  const { stepExpression, useLinear, minColor, maxColor } = computeDiscreteColorBands(
    layer,
    getPaletteColors,
    mapIntervalToColorIndex,
    getDefaultPalette,
    getLayerNumClasses(layer)
  );

  // Constant color (single value / uniform metric)
  if (typeof stepExpression === 'string') {
    return stepExpression;
  }

  // Discrete ``step`` or categorical ``match`` expression
  if (useLinear || !stepExpression) {
    if (min >= max) {
      return minColor || '#440154';
    }
    const colorProp = getLayerColorProperty(layer) || 'value';
    return [
      'interpolate',
      ['linear'],
      propertyGetExpression(colorProp),
      min, minColor || '#440154',
      max, maxColor || '#440154'
    ];
  }

  return stepExpression;
};

const MAP_CONTROL_SHELL =
  'bg-white dark:bg-gray-900 rounded-lg shadow-md border border-gray-200 dark:border-gray-700';

const MAP_CONTROL_COLLAPSED =
  'w-12 h-12 p-1 flex items-center justify-center overflow-hidden';

const MapPanel = ({
  mapRef,
  mapLayers,
  maxFeaturesPerLayer = MAP_MAX_FEATURES_PER_LAYER_DEFAULT,
  hoverInfo,
  selectedInfo,
  onMouseMove,
  onMapClick,
  onToggleLayerVisibility,
  onRemoveLayer,
  onUpdateLayerTheme,
  onOpenBuilder,
  onMapLoad,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const [selectedBasemap, setSelectedBasemap] = useState('light');
  const [isBasemapMenuOpen, setIsBasemapMenuOpen] = useState(false);
  const [mapUnavailable, setMapUnavailable] = useState(false);
  const basemapMenuRef = useRef(null);
  const searchControlRef = useRef(null);
  const searchInputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (basemapMenuRef.current && !basemapMenuRef.current.contains(event.target)) {
        setIsBasemapMenuOpen(false);
      }
      if (
        searchControlRef.current &&
        !searchControlRef.current.contains(event.target) &&
        !searchQuery.trim()
      ) {
        setIsSearchExpanded(false);
      }
    };

    if (isBasemapMenuOpen || isSearchExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isBasemapMenuOpen, isSearchExpanded, searchQuery]);

  useEffect(() => {
    if (isSearchExpanded) {
      searchInputRef.current?.focus();
    }
  }, [isSearchExpanded]);

  const layersForMap = useMemo(() => {
    return mapLayers
      .slice()
      .reverse()
      .filter((layer) => layer.data && layer.status !== 'loading' && layer.status !== 'error')
      .map((layer) => {
        const full = prepareGeoJSONForMap(layer.data);
        const displayData = sliceGeoJsonForDisplay(full, maxFeaturesPerLayer);
        return { layer, displayData };
      });
  }, [mapLayers, maxFeaturesPerLayer]);

  const moveDataLayersToTop = useCallback((map) => {
    if (!map?.isStyleLoaded?.()) return;

    layersForMap.forEach(({ layer }) => {
      for (const suffix of ['fill', 'circle', 'line']) {
        const layerId = `l-${layer.id}-${suffix}`;
        if (map.getLayer(layerId)) {
          try {
            map.moveLayer(layerId);
          } catch {
            // Layer may not be ready to move yet.
          }
        }
      }
    });
  }, [layersForMap]);

  const handleGeocode = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim() || isSearching) return;

    setIsSearching(true);
    setSearchError(null);

    try {
      const result = await mapApi.geocode(searchQuery.trim());

      if (mapRef.current && result.latitude && result.longitude) {
        // Use getMap() to access the underlying MapLibre GL map instance
        const map = mapRef.current.getMap ? mapRef.current.getMap() : mapRef.current;
        if (map && typeof map.flyTo === 'function') {
          map.flyTo({
            center: [result.longitude, result.latitude],
            zoom: 10,
            duration: 2500,
            curve: 1.42 // Smooth curve for natural easing (default is 1.42)
          });
        } else if (map && typeof map.easeTo === 'function') {
          // Fallback to easeTo if flyTo is not available - smoother but no curve
          map.easeTo({
            center: [result.longitude, result.latitude],
            zoom: 10,
            duration: 2500
          });
        }
      }

      setSearchQuery('');
      setIsSearchExpanded(false);
    } catch (error) {
      console.error('Geocoding error:', error);
      setSearchError(error.response?.data?.detail || 'Failed to find location');
    } finally {
      setIsSearching(false);
    }
  };

  return (
    <div className="h-full w-full bg-white dark:bg-gray-950 relative">
      {/* Map controls: search above basemap */}
      <div className="absolute bottom-4 left-4 z-40 flex flex-col gap-2 pointer-events-none">
        <div className="pointer-events-auto" ref={searchControlRef}>
          {isSearchExpanded ? (
            <form onSubmit={handleGeocode} className="w-64">
              <div className="relative">
                <div
                  className={cn(
                    MAP_CONTROL_SHELL,
                    'flex items-center overflow-hidden transition-all duration-200 focus-within:border-emerald-400 focus-within:shadow-xl focus-within:ring-2 focus-within:ring-emerald-400/20'
                  )}
                >
                  <div className="pl-3 pr-1.5 flex items-center">
                    {isSearching ? (
                      <Loader2 className="w-3.5 h-3.5 text-emerald-500 dark:text-emerald-400 animate-spin" />
                    ) : (
                      <Search className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                    )}
                  </div>
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setSearchError(null);
                    }}
                    placeholder="Search for a location..."
                    className="flex-1 bg-transparent border-0 outline-none px-2 py-2 text-xs text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-0"
                    disabled={isSearching}
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={() => {
                        setSearchQuery('');
                        setSearchError(null);
                      }}
                      className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                      disabled={isSearching}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
                {searchError && (
                  <div className="mt-2 px-3 py-1.5 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg text-xs text-red-600 dark:text-red-400">
                    {searchError}
                  </div>
                )}
              </div>
            </form>
          ) : (
            <button
              type="button"
              onClick={() => {
                setIsBasemapMenuOpen(false);
                setIsSearchExpanded(true);
              }}
              className={cn(
                MAP_CONTROL_SHELL,
                MAP_CONTROL_COLLAPSED,
                'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors'
              )}
              aria-label="Search for a location"
            >
              <Search className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="relative pointer-events-auto" ref={basemapMenuRef}>
          <button
            type="button"
            onClick={() => {
              setIsSearchExpanded(false);
              setIsBasemapMenuOpen(!isBasemapMenuOpen);
            }}
            className={cn(
              MAP_CONTROL_SHELL,
              MAP_CONTROL_COLLAPSED,
              'hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors'
            )}
            aria-label={`Basemap: ${BASEMAPS[selectedBasemap].name}`}
          >
            <Globe2 className="w-4 h-4 text-gray-600 dark:text-gray-300" />
          </button>
          {isBasemapMenuOpen && (
            <div className="absolute left-full bottom-0 ml-2 bg-white dark:bg-gray-900 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden min-w-[200px]">
              <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                  Choose basemap
                </p>
              </div>
              {Object.values(BASEMAPS).map((basemap) => (
                <button
                  key={basemap.id}
                  type="button"
                  onClick={() => {
                    setSelectedBasemap(basemap.id);
                    setIsBasemapMenuOpen(false);
                  }}
                  className={cn(
                    'w-full text-left px-3 py-2 text-sm font-medium transition-colors',
                    selectedBasemap === basemap.id
                      ? 'bg-emerald-50 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-400'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                  )}
                >
                  {basemap.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <Map
        ref={mapRef}
        initialViewState={{ longitude: 0, latitude: 20, zoom: 2 }}
        style={{ width: '100%', height: '100%' }}
        mapStyle={BASEMAPS[selectedBasemap].style}
        mapLibreGL={maplibregl}
        preserveDrawingBuffer={true}
        onError={(event) => {
          const detail = String(event?.error?.message || event?.error || '');
          if (/webgl/i.test(detail)) {
            setMapUnavailable(true);
            return;
          }
          console.error(event?.error || event);
        }}
        onLoad={(event) => {
          moveDataLayersToTop(event.target);
          onMapLoad?.(event.target);
        }}
        onStyleData={(event) => moveDataLayersToTop(event.target)}
        interactiveLayerIds={mapLayers.filter(l => l.visible).flatMap(l => [
          `l-${l.id}-fill`,
          `l-${l.id}-circle`,
          `l-${l.id}-line`
        ])}
        onMouseMove={onMouseMove}
        onClick={onMapClick}
      >
        {layersForMap.map(({ layer, displayData }) => {
            // Filter data based on valueFilter - only show geometries in selected range
            const data = displayData;

            // Build filter condition for MapLibre filter property
            const filterCondition = buildFilterCondition(layer.valueFilter, layer);

            // Simple opacity functions - no conditional logic needed since we filter
            const getFillOpacity = () => 0.8;
            const getLineOpacity = () => 0.4;
            const getLineWidth = () => 0.5;
            const getLineColor = () => '#ffffff';

            const firstGeom = data.features[0]?.geometry?.type;
            const isPointLayer = firstGeom && ['Point', 'MultiPoint'].includes(firstGeom);
            const isLineLayer = firstGeom && ['LineString', 'MultiLineString'].includes(firstGeom);

            return (
              <Source key={`s-${layer.id}`} id={`s-${layer.id}`} type="geojson" data={data}>
                <Layer
                  id={`l-${layer.id}-fill`}
                  type="fill"
                  {...(filterCondition ? { filter: filterCondition } : {})}
                  layout={{
                    visibility: (layer.visible && !isPointLayer && !isLineLayer) ? 'visible' : 'none'
                  }}
                  paint={{
                    'fill-color': getLayerColor(layer),
                    'fill-opacity': getFillOpacity()
                  }}
                />
                {/* Circle layer for points */}
                <Layer
                  id={`l-${layer.id}-circle`}
                  type="circle"
                  {...(filterCondition ? { filter: filterCondition } : {})}
                  layout={{
                    visibility: (layer.visible && isPointLayer) ? 'visible' : 'none'
                  }}
                  paint={{
                    'circle-color': getLayerColor(layer),
                    'circle-radius': [
                      'interpolate',
                      ['linear'],
                      ['zoom'],
                      0, 2,
                      4, 4,
                      12, 12
                    ],
                    'circle-opacity': getFillOpacity()
                  }}
                />
                <Layer
                  id={`l-${layer.id}-line`}
                  type="line"
                  {...(filterCondition ? { filter: filterCondition } : {})}
                  layout={{
                    visibility: (layer.visible && !isPointLayer) ? 'visible' : 'none',
                    'line-join': 'round',
                    'line-cap': 'round'
                  }}
                  paint={{
                    'line-color': isLineLayer ? getLayerColor(layer) : getLineColor(),
                    'line-width': isLineLayer ? [
                      'interpolate',
                      ['linear'],
                      ['zoom'],
                      0, 1,
                      4, 2,
                      12, 4
                    ] : getLineWidth(),
                    'line-opacity': isLineLayer ? 0.8 : getLineOpacity()
                  }}
                />
              </Source>
            );
          })}

        <MapFeaturePopup
          hoverInfo={hoverInfo}
          selectedInfo={selectedInfo}
          mapLayers={mapLayers}
        />
      </Map>

      {mapUnavailable && (
        <div
          role="alert"
          className="absolute inset-0 z-50 flex items-center justify-center bg-white px-6 text-center dark:bg-gray-950"
        >
          <p className="max-w-sm text-sm leading-relaxed text-gray-600 dark:text-gray-300">
            {MAP_WEBGL_UNAVAILABLE_MESSAGE}
          </p>
        </div>
      )}

      {/* Empty state message when no layers exist - Add Layer button hidden */}
      {
        mapLayers.filter(l => l.id !== 'preview-layer').length === 0 && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none z-10">
            {/* Add Layer button hidden */}
          </div>
        )
      }
    </div >
  );
};

export default MapPanel;
